// Turn-level status surfaces shared by the widget and the dashboard — what a
// reply shows when the assistant stalls. Presentational: `useChat` decides
// WHEN a turn is retrying or failed (see turnRetry.ts); hosts render these and
// pass `chat.retry` through. Hosts load the matching styles:
// `@netwatchai/chat-core/turn-status.css` (the widget inlines it into its
// shadow DOM). Colours follow the chat-content contract — host `--nw-*` vars
// with dark fallbacks — so both themes work without a host-side override.
import type { TurnFailure } from './types'

export interface RetryingNoticeProps {
  /** The send now in flight (2 = first automatic re-send). */
  attempt: number
}

/** Shown in place of the usual thinking hint while a stalled turn is re-sent,
 *  so the wait reads as deliberate rather than as the same hang continuing. */
export function RetryingNotice({ attempt }: RetryingNoticeProps) {
  return (
    <div className="nw-turn-retrying" role="status" aria-live="polite">
      <span className="nw-turn-spinner" aria-hidden="true" />
      <span className="nw-turn-retrying-text">
        Taking longer than expected — retrying (attempt {attempt})…
      </span>
    </div>
  )
}

export interface FailedTurnProps {
  failure: TurnFailure
  /** Re-send the turn. Omit to render the failure without a button. */
  onRetry?: () => void
  /** Disable the button (another turn is in flight). */
  disabled?: boolean
}

function IconWarn() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}

function IconRetry() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  )
}

/** The failed-turn card: what went wrong, and a Retry button that re-sends
 *  the same message into the same bubble. */
export function FailedTurn({ failure, onRetry, disabled }: FailedTurnProps) {
  const title =
    failure.reason === 'stall' ? 'Quark stopped responding' : 'Quark couldn’t answer'
  return (
    <div className={`nw-turn-failed is-${failure.reason}`} role="alert">
      <span className="nw-turn-failed-icon">
        <IconWarn />
      </span>
      <div className="nw-turn-failed-body">
        <div className="nw-turn-failed-title">{title}</div>
        {failure.detail && <div className="nw-turn-failed-detail">{failure.detail}</div>}
      </div>
      {onRetry && (
        <button type="button" className="nw-turn-retry" onClick={onRetry} disabled={disabled}>
          <IconRetry />
          Retry
        </button>
      )}
    </div>
  )
}
